import React, {useEffect, useState} from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate, useNavigate} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Import modernized theme
import theme from './theme';

// Context providers
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from "./context/LanguageContext";

// Page components
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import HomePage from './pages/HomePage';
import ContentExplorerPage from './pages/ContentExplorerPage';
import VocabularyPage from './pages/VocabularyPage';
import VocabularyReviewPage from './pages/VocabularyReviewPage';
import ProgressPage from './pages/ProgressPage';
import ProfilePage from './pages/ProfilePage';
import LearningSessionPage from './pages/LearningSessionPage';
import NotFoundPage from './pages/NotFoundPage';
import SessionsListPage from './pages/SessionsListPage';
import SessionReviewPage from './pages/SessionReviewPage';


//Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import PathList from './pages/admin/PathList';
import PathForm from './pages/admin/PathForm';
import PathDetail from './pages/admin/PathDetail';
import StoryList from './pages/admin/StoryList';
import StoryForm from './pages/admin/StoryForm';
import StoryDetail from './pages/admin/StoryDetail';
import UserManagement from './pages/admin/UserManagement';
import AdminSettings from './pages/admin/AdminSettings';
import StoryProductionCenter from './pages/admin/StoryProductionCenter'
import ContentMatrix from './pages/admin/ContentMatrix';
import LibraryManager from './pages/admin/LibraryManager';
import WordMatchingWorkbench from './pages/admin/WordMatchingWorkbench';

import BreadcrumbsPathList from './pages/BreadcrumbsPathList';
import BreadcrumbsPathDetail from './pages/BreadcrumbsPathDetail';
import BreadcrumbsStoryDetail from './pages/BreadcrumbsStoryDetail';

// Loading component for better UX during auth checks
import {Box, CircularProgress, Typography} from '@mui/material';
import {SettingsProvider} from "./context/SettingsContext";
import apiClient from "./services/apiClient";
import AdminLayout from "./components/AdminLayout";
import JournalPage from "./pages/JournalPage";

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // Enhanced loading state while authentication is being checked
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: 'background.default'
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
        <Typography variant="h6" color="text.secondary">Loading your experience...</Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Render children if authenticated
  return children;
};

// Public route component (redirects to home if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // Enhanced loading state while authentication is being checked
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: 'background.default'
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
        <Typography variant="h6" color="text.secondary">Preparing Magellan...</Typography>
      </Box>
    );
  }

  // Redirect to home if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" />;
  }

  // Render children if not authenticated
  return children;
};

function App() {

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <LanguageProvider>
              <SettingsProvider>
                    <Routes>
                      {/* Public routes */}
                      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
                      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

                      {/* Protected routes with Layout */}
                      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                        <Route index element={<HomePage />} />
                        <Route path="explore" element={<ContentExplorerPage />} />
                        {/* Conversations & Logbook are the SAME screen (the one Andrew prefers).
                            /conversations just opens it pre-filtered to conversations. */}
                        <Route path="conversations" element={<SessionsListPage defaultFilter="conversation" title="Conversations" />} />
                        <Route path="vocabulary" element={<VocabularyPage />} />
                        <Route path="vocabulary/review" element={<VocabularyReviewPage />} />
                        <Route path="progress" element={<ProgressPage />} />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route path="session/:sessionId" element={<LearningSessionPage />} />
                        {/* Legacy /continue removed — the single conversation surface is /session/:id.
                            Kept as a redirect so old links/bookmarks resolve to the session list. */}
                        <Route path="/continue" element={<Navigate to="/sessions" replace />} />
                        {/* Session Review Routes */}
                        <Route path="sessions" element={<SessionsListPage />} />
                        <Route path="sessions/:sessionId" element={<SessionReviewPage />} />
                        <Route path="journal" element={<JournalPage />} />
                        <Route path="*" element={<NotFoundPage />} />

                        {/* Breadcrumbs routes */}
                        <Route path="/breadcrumbs" element={<BreadcrumbsPathList />} />
                        <Route path="/breadcrumbs/paths/:pathId" element={<BreadcrumbsPathDetail />} />
                        <Route path="/breadcrumbs/stories/:storyId" element={<BreadcrumbsStoryDetail />} />
                      </Route>

                      {/* Admin routes - protected by RequireAdmin */}
                      <Route element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/admin/paths" element={<PathList />} />
                        <Route path="/admin/paths/new" element={<PathForm />} />
                        <Route path="/admin/paths/:pathId" element={<PathDetail />} />
                        <Route path="/admin/paths/:pathId/matrix" element={<ContentMatrix />} />
                        <Route path="/admin/paths/:pathId/edit" element={<PathForm />} />
                        <Route path="/admin/stories" element={<StoryList />} />
                        <Route path="/admin/stories/new" element={<StoryForm />} />
                        <Route path="/admin/stories/:storyId" element={<StoryDetail />} />
                        <Route path="/admin/stories/:storyId/edit" element={<StoryForm />} />
                        <Route path="/admin/stories/:storyId/translations" element={<StoryProductionCenter />} />
                        <Route path="/admin/stories/:storyId/word-matching" element={<WordMatchingWorkbench />} />
                        {/* Alias for the old storyCreator URL (kept so existing links/bookmarks resolve) */}
                        <Route path="/admin/stories/:storyId/storyCreator" element={<StoryProductionCenter />} />
                        <Route path="/admin/library" element={<LibraryManager />} />
                        <Route path="/admin/users" element={<UserManagement />} />
                        <Route path="/admin/settings" element={<AdminSettings />} />
                      </Route>


                    </Routes>
              </SettingsProvider>
          </LanguageProvider>
        </AuthProvider>

      </Router>
    </ThemeProvider>
  );
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const response = await apiClient.admin.checkAuthorization();
        console.log('user response: ' + response);
        setIsAdmin(response.data.is_admin);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        navigate('/');
      } finally {
        setCheckingAdmin(false);
      }
    };

    if (!loading) {
      checkAdminStatus();
    }
  }, [user, loading, navigate]);

  if (loading || checkingAdmin) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return isAdmin ? children : <Navigate to="/" replace />;
}

export default App;